import React, { useState, useEffect } from 'react';
import uploadToDropbox from '../services/dropboxService';
import { createMagazine, getMagazines, updateMagazine, deleteMagazine } from '../services/magazineService';
import {
  PageHeader,
  Panel,
  PanelHeader,
  Modal,
  ConfirmDialog,
  EmptyState,
} from '../components/admin';

function Magazine() {
  const [bookData, setBookData] = useState({ title: '', description: '', pdfFile: null });
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [magazines, setMagazines] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchMagazines();
  }, []);

  const fetchMagazines = async () => {
    try {
      const data = await getMagazines();
      setMagazines(data);
    } catch (err) {
      setError('Failed to fetch magazines: ' + err.message);
    }
  };

  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    setBookData((prev) => ({ ...prev, pdfFile: file }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bookData.pdfFile && !editingId) {
      setError('Please select a PDF file');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      let pdfUrl = editingId ? magazines.find((m) => m.id === editingId).bookUrl : null;

      if (bookData.pdfFile) {
        pdfUrl = await uploadToDropbox(bookData.pdfFile);
      }

      const finalBookData = {
        bookName: bookData.title,
        description: bookData.description,
        bookUrl: pdfUrl,
      };

      if (editingId) {
        await updateMagazine(editingId, finalBookData);
      } else {
        await createMagazine(finalBookData);
      }

      await fetchMagazines();
      setBookData({ title: '', description: '', pdfFile: null });
      setEditingId(null);
      setShowForm(false);
    } catch (err) {
      setError('Failed to save magazine: ' + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = (magazine) => {
    setBookData({ title: magazine.bookName, description: magazine.description, pdfFile: null });
    setEditingId(magazine.id);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMagazine(deleting.id);
      await fetchMagazines();
      setDeleting(null);
    } catch (err) {
      setError('Failed to delete magazine: ' + err.message);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Magazines"
        description="Publish digital magazine editions"
        actions={
          !showForm && (
            <button type="button" className="btn-primary" onClick={() => setShowForm(true)}>
              Add new magazine
            </button>
          )
        }
      />

      {error && (
        <div className="panel px-4 py-3 text-sm text-accent-soft border-accent-dim">{error}</div>
      )}

      {showForm && (
        <Panel>
          <PanelHeader
            title={editingId ? 'Edit magazine' : 'Add new magazine'}
            actions={
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setEditingId(null);
                  setBookData({ title: '', description: '', pdfFile: null });
                  setShowForm(false);
                }}
              >
                Close
              </button>
            }
          />
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            <div>
              <label className="field-label">Title</label>
              <input
                type="text"
                className="field"
                value={bookData.title}
                onChange={(e) => setBookData((prev) => ({ ...prev, title: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="field-label">Description</label>
              <textarea
                className="field min-h-[96px]"
                value={bookData.description}
                onChange={(e) => setBookData((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div>
              <label className="field-label">PDF file {editingId && '(leave empty to keep current)'}</label>
              <input
                type="file"
                accept=".pdf"
                onChange={handlePdfUpload}
                className="field !py-2.5 file:mr-3 file:rounded-md file:border-0 file:bg-surface-600 file:px-3 file:py-1.5 file:text-white hover:file:bg-surface-700"
                disabled={isUploading}
              />
              {bookData.pdfFile && (
                <p className="mt-1.5 text-xs text-gray-500">Selected: {bookData.pdfFile.name}</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="submit" className="btn-primary" disabled={isUploading}>
                {isUploading ? 'Saving…' : editingId ? 'Update magazine' : 'Upload magazine'}
              </button>
            </div>
          </form>
        </Panel>
      )}

      <Panel padded={false}>
        <PanelHeader
          title="Published magazines"
          description={`${magazines.length} edition${magazines.length === 1 ? '' : 's'}`}
        />
        {magazines.length === 0 ? (
          <EmptyState
            title="No magazines yet"
            message="Upload your first PDF edition to publish it to readers."
          />
        ) : (
          <ul className="divide-y divide-line">
            {magazines.map((magazine) => (
              <li key={magazine.id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4">
                <div className="w-10 h-10 rounded-lg bg-surface-700 border border-line-strong flex items-center justify-center text-gray-400 shrink-0">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{magazine.bookName}</p>
                  <p className="text-sm text-gray-500 truncate">{magazine.description}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a href={magazine.bookUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-1.5 !px-3">
                    View PDF
                  </a>
                  <button type="button" className="btn-ghost !py-1.5 !px-3" onClick={() => handleEdit(magazine)}>
                    Edit
                  </button>
                  <button type="button" className="btn-danger !py-1.5 !px-3" onClick={() => setDeleting(magazine)}>
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {deleting && (
        <ConfirmDialog
          title="Delete magazine"
          message={`Delete "${deleting.bookName}"? This cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

export default Magazine;
